import { useMutation } from '@tanstack/react-query'
import { Alert, Button, Form } from 'antd'
import { Save, ShieldCheck } from 'lucide-react'
import type { PermissionCode } from '@super-mega/server/contracts/authorization'
import { botPermissionDefinitions } from '@super-mega/server/contracts/bot'
import { PermissionPicker } from '@/features/authorization/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import type { BotDetailData } from './bot-detail.tsx'

export function BotPermissions({ bot, onSaved }: { bot: BotDetailData; onSaved: () => Promise<void> }) {
  const trpc = useTRPC()
  const access = useAccess()
  const permissions = useMutation(trpc.bot.permissions.mutationOptions({ onSuccess: onSaved }))
  return (
    <div className="space-y-5">
      {permissions.error && <Alert type="error" showIcon title={permissions.error.message} />}
      <section className="bg-surface border-border rounded-xl border">
        <div className="border-border flex items-start gap-3 border-b p-5 sm:px-6">
          <ShieldCheck className="text-accent mt-0.5 size-5 shrink-0" />
          <div>
            <h2 className="text-content m-0 text-base font-semibold">业务权限</h2>
            <p className="text-content-muted mt-1.5 mb-0 text-sm leading-6">
              按业务模块配置机器人可执行的操作，保存后生效。
            </p>
          </div>
        </div>
        <Form<{ permissionCodes: PermissionCode[] }>
          layout="vertical"
          initialValues={{ permissionCodes: bot.permissionCodes }}
          onFinish={({ permissionCodes }) =>
            permissions.mutate({
              id: bot.id,
              expectedVersion: bot.version,
              permissionCodes,
              catalogVersion: bot.catalogVersion
            })
          }
        >
          <div className="p-5 sm:p-6">
            <Form.Item name="permissionCodes" className="m-0!">
              <PermissionPicker
                definitions={botPermissionDefinitions}
                disabled={!access.can('bot.permission.update') || permissions.isPending}
              />
            </Form.Item>
          </div>
          {access.can('bot.permission.update') && (
            <div className="bg-surface border-border sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-b-xl border-t px-5 py-4 sm:px-6">
              <span className="text-content-soft text-xs">仅授予机器人开展业务所需的权限</span>
              <Button
                type="primary"
                htmlType="submit"
                icon={<Save className="size-4" />}
                loading={permissions.isPending}
              >
                保存权限
              </Button>
            </div>
          )}
        </Form>
      </section>
    </div>
  )
}
